"use client"

import { Check, ChevronDown, Languages } from "lucide-react"
import { useMemo, useState } from "react"
import { useLanguage } from "@/components/i18n/language-provider"
import { SUPPORTED_LOCALES } from "@/lib/i18n"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer"
import { useIsMobile } from "@/hooks/use-mobile"

type LanguageSwitcherProps = {
  className?: string
  onChange?: (locale: string) => void
  compact?: boolean | "auto"
  fullWidth?: boolean
}

export function LanguageSwitcher({ className, onChange, compact = "auto", fullWidth = false }: LanguageSwitcherProps) {
  const { locale, setLocale, t } = useLanguage()
  const [open, setOpen] = useState(false)
  const isMobile = useIsMobile()

  const currentOption = useMemo(
    () => SUPPORTED_LOCALES.find((option) => option.code === locale) ?? SUPPORTED_LOCALES[0],
    [locale],
  )

  const shouldCompact = compact === true || (compact === "auto" && isMobile)

  const handleChange = (nextLocale: string) => {
    setLocale(nextLocale)
    onChange?.(nextLocale)
    setOpen(false)
  }

  // Toujours compact : drapeau seul, jamais le grand bouton
  const trigger = (
    <Button
      type="button"
      variant="outline"
      className={cn(
        "group border-border/60 bg-background/85 text-foreground rounded-full backdrop-blur-md shadow-sm transition-all hover:bg-background hover:shadow-md focus-visible:ring-2 focus-visible:ring-primary/70 focus-visible:ring-offset-2 h-9 w-9 min-w-0 px-0 flex items-center justify-center",
        className,
      )}
      aria-label={t("changeLanguage")}
      tabIndex={0}
    >
      <span className="inline-flex items-center justify-center w-full">
        <span className="text-xl leading-none" aria-hidden>
          {currentOption.flag}
        </span>
      </span>
    </Button>
  )

  const optionList = SUPPORTED_LOCALES.map((option) => {
    const selected = option.code === locale

    return (
      <button
        key={option.code}
        type="button"
        onClick={() => handleChange(option.code)}
        className={cn(
          "flex w-full items-center justify-between rounded-xl border px-3 py-2.5 text-left transition-colors",
          selected
            ? "border-primary/30 bg-primary/10 text-primary"
            : "border-transparent bg-muted/40 text-foreground hover:bg-muted/70",
        )}
        aria-current={selected ? "true" : undefined}
      >
        <span className="inline-flex min-w-0 items-center gap-2.5">
          <span className="text-base leading-none" aria-hidden>
            {option.flag}
          </span>
          <span className="min-w-0 leading-tight">
            <span className="block truncate text-sm font-semibold">{option.nativeLabel}</span>
            <span className="text-muted-foreground block text-[11px]">{option.label}</span>
          </span>
        </span>
        {selected ? <Check className="h-4 w-4 shrink-0" /> : null}
      </button>
    )
  })

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerTrigger asChild>{trigger}</DrawerTrigger>
        <DrawerContent className="rounded-t-3xl border-border/60">
          <DrawerHeader className="text-left">
            <DrawerTitle className="flex items-center gap-2 text-base">
              <Languages className="h-4 w-4" />
              {t("selectLanguage")}
            </DrawerTitle>
            <DrawerDescription>{t("languageHint")}</DrawerDescription>
          </DrawerHeader>
          <div className="max-h-[52vh] space-y-1.5 overflow-y-auto px-3 pb-5">{optionList}</div>
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[290px] rounded-2xl border-border/60 p-2">
        <DropdownMenuLabel className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <Languages className="h-3.5 w-3.5" />
          {t("selectLanguage")}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {SUPPORTED_LOCALES.map((option) => {
          const selected = option.code === locale

          return (
            <DropdownMenuItem
              key={option.code}
              onClick={() => handleChange(option.code)}
              className={cn(
                "rounded-xl px-3 py-2.5",
                selected ? "bg-primary/10 text-primary focus:bg-primary/15" : "focus:bg-muted",
              )}
            >
              <span className="mr-2 text-base leading-none" aria-hidden>
                {option.flag}
              </span>
              <span className="min-w-0 flex-1 leading-tight">
                <span className="block truncate text-sm font-semibold">{option.nativeLabel}</span>
                <span className="text-muted-foreground block truncate text-[11px]">{option.label}</span>
              </span>
              {selected ? <Check className="h-4 w-4 shrink-0" /> : null}
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
